import type { ArcadeGame } from '../../../game';
import type { SceneEnvironment } from '../../../game';
import type { GameModeId } from '../../../shared/config';
import type { ArcadeSceneBoot } from '../../../app/boot';
import { syncHudDom, type HudDomRefs } from './syncHudDom';

export interface RenderLoopDeps {
  boot: ArcadeSceneBoot;
  getArcade: () => ArcadeGame | null;
  getSceneEnv: () => SceneEnvironment | null;
  getGameMode: () => GameModeId;
  hudRefs: HudDomRefs;
  /** Optional hook each frame (e.g. host bookkeeping). */
  onFrameScheduled?: (id: number) => void;
}

export interface RenderLoopHandle {
  stop: () => void;
}

/**
 * Main rAF loop: arcade update, idle cube/camera, scene env, render.
 * Extracted from App — same order of operations as before.
 */
export function startRenderLoop(deps: RenderLoopDeps): RenderLoopHandle {
  const { boot, getArcade, getSceneEnv, getGameMode, hudRefs, onFrameScheduled } = deps;
  let idleTime = 0;
  let stopped = false;
  let frameId: number | null = null;

  const render = () => {
    if (stopped) return;
    frameId = requestAnimationFrame(render);
    onFrameScheduled?.(frameId);

    const dt = Math.min(boot.clock.getDelta(), 0.05);
    const arcade = getArcade();
    if (!arcade) {
      return;
    }

    const phase = arcade.update(dt, boot.mouse, boot.cameraCenter);
    syncHudDom(arcade, phase, getGameMode(), hudRefs);

    if (phase === 'idle' || phase === 'gameover') {
      boot.idleCube.rotation.x += 0.01;
      boot.idleCube.rotation.y += 0.01;
      boot.updateIdleCamera();
      idleTime += dt;
      boot.gridMaterialFloor.uniforms.uTime.value = idleTime;
      boot.gridMaterialCeiling.uniforms.uTime.value = idleTime;
    }
    getSceneEnv()?.update(boot.camera, dt);

    boot.camera.aspect = window.innerWidth / window.innerHeight;
    boot.camera.updateProjectionMatrix();
    boot.renderer.setSize(window.innerWidth, window.innerHeight);
    boot.renderer.render(boot.scene, boot.camera);
  };

  render();

  return {
    stop: () => {
      stopped = true;
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }
    },
  };
}
